import type {ReferencePlane} from '../../reference/ReferencePlane';
import {isStaleWorkerResponseError} from '../../worker/StaleWorkerResponseError';
import {
    type SketchGroupEndResult,
    type SketchGroupManager,
} from './SketchGroupManager';

/**
 * 草图会话结束选项。
 */
export interface SketchSessionTerminationOptions {
    /** 是否在会话结束时丢弃空草图。 */
    discardEmpty?: boolean;
}

/**
 * 草图会话结束结果。
 */
export interface SketchSessionTerminationResult {
    /** 已结束的草图组 ID。 */
    endedGroupId: string | null;
    /** 草图组是否被移除。 */
    removed: boolean;
    /** 是否已完成定义提交。 */
    committed: boolean;
    /** 被回收的临时基准面 ID。 */
    removedTemporaryPlaneId: number | null;
}

/**
 * 草图会话终止器依赖。
 */
export interface SketchSessionTerminationDependencies {
    /** 草图组管理器。 */
    sketchGroupManager: SketchGroupManager;
    /** 提交草图定义变更。 */
    commitSketchDefinitionChanges: (sketchId: string) => Promise<boolean>;
    /** 在发布结束生命周期事件前收口最终显示态。 */
    finalizeEndedGroupBeforeLifecycle?: (endResult: SketchGroupEndResult) => Promise<void> | void;
    /** 回收临时基准面。 */
    removeDatumPlane: (planeId: number) => void;
    /** 判断基准面是否为临时平面。 */
    isTemporaryDatumPlane: (planeId: number) => boolean;
    /** 读取当前正处于法向视图的参考面。 */
    getCurrentReferencePlane: () => ReferencePlane | null;
    /** 退出基准面法向视图。 */
    exitDatumPlaneView: () => void;
}

/**
 * 草图会话终止器。
 *
 * 职责：
 * 1. 统一编排“结束草图组 -> 提交定义 -> 回收临时平面 -> 发布生命周期事件”；
 * 2. 保证 `cad-core` 自己闭合资源生命周期，不把清理责任泄漏给宿主层；
 * 3. 让宿主层只消费最终结果，而不是再手工拼接提交与设计树刷新时序。
 */
export class SketchSessionTerminator {
    /**
     * @param dependencies 终止器依赖。
     */
    public constructor(private readonly dependencies: SketchSessionTerminationDependencies) {}

    /**
     * 终止当前草图会话。
     *
     * @param options 终止选项。
     * @returns 终止结果。
     */
    public async terminate(
        options: SketchSessionTerminationOptions = {},
    ): Promise<SketchSessionTerminationResult> {
        const activeSketchGroup = this.dependencies.sketchGroupManager.activeSketchGroup;
        if (!activeSketchGroup) {
            return {
                endedGroupId: null,
                removed: false,
                committed: false,
                removedTemporaryPlaneId: null,
            };
        }

        const activeReferencePlane = activeSketchGroup.referencePlane;
        const temporaryPlaneId = this.resolveTemporaryPlaneId(activeReferencePlane);
        if (temporaryPlaneId !== null && this.dependencies.getCurrentReferencePlane()?.id === temporaryPlaneId) {
            this.dependencies.exitDatumPlaneView();
        }

        const endResult = this.dependencies.sketchGroupManager.endGroup({
            discardEmpty: options.discardEmpty,
            publishLifecycleEvents: false,
            selectEndedObjects: false,
        });
        
        /**
         * 非空实体面草图退出后仍需要原宿主参考面参与后续“编辑草图”和绘制投影。
         * 因此只有草图本身被丢弃时，才回收创建草图时生成的临时宿主平面。
         */
        const removedTemporaryPlaneId = temporaryPlaneId !== null && endResult.removed
            ? temporaryPlaneId
            : null;
        

        let committed = false;
        try {
            committed = await this.commitIfNeeded(endResult);
            return {
                endedGroupId: endResult.endedGroupId,
                removed: endResult.removed,
                committed,
                removedTemporaryPlaneId,
            };
        } finally {
            if (removedTemporaryPlaneId !== null) {
                this.dependencies.removeDatumPlane(removedTemporaryPlaneId);
            }
            let finalizeError: unknown = null;
            try {
                await this.dependencies.finalizeEndedGroupBeforeLifecycle?.(endResult);
            } catch (error) {
                /**
                 * 退出草图收尾只负责同步显示态和发布生命周期事件。
                 * Worker stale 表示旧重建读请求已被更新 revision 淘汰，不能让派生状态刷新失败反向阻断草图退出。
                 * 但若 Finalizer 已恢复原草图 Edit，会话事实表明本次退出没有完成，
                 * 即使异常类型是 stale 也必须继续向上抛出，禁止 web-client 提交 idle。
                 */
                const restoredOriginalGroup = (
                    endResult.endedGroupId !== null
                    && this.dependencies.sketchGroupManager.activeSketchGroup?.uuid
                        === endResult.endedGroupId
                );
                if (restoredOriginalGroup || !isStaleWorkerResponseError(error)) {
                    finalizeError = error;
                }
            }
            /**
             * Finalizer 可以在退出恢复失败后重新激活原草图。
             * 活动草图是会话是否真正结束的领域事实，不能再发布互相矛盾的 Ended。
             */
            const restoredEditingGroupId =
                this.dependencies.sketchGroupManager.activeSketchGroup?.uuid ?? null;
            if (restoredEditingGroupId !== endResult.endedGroupId) {
                this.dependencies.sketchGroupManager.selectEndedGroupSketchObjects(endResult.endedGroupId);
                this.dependencies.sketchGroupManager.publishEndGroupLifecycle(endResult);
            }
            if (finalizeError) {
                throw finalizeError;
            }
        }
    }

    /**
     * 判断当前会话是否需要回收临时基准面。
     *
     * @param referencePlane 活动参考面。
     * @returns 临时平面 ID；非临时平面返回 null。
     */
    private resolveTemporaryPlaneId(referencePlane: ReferencePlane): number | null {
        if (!this.dependencies.isTemporaryDatumPlane(referencePlane.id)) {
            return null;
        }
        return referencePlane.id;
    }

    /**
     * 按结束结果决定是否需要触发一次草图定义提交。
     *
     * @param endResult 草图组结束结果。
     * @returns 是否提交成功。
     */
    private async commitIfNeeded(endResult: SketchGroupEndResult): Promise<boolean> {
        if (!endResult.shouldCommitDefinitionChanges || !endResult.endedGroupId) {
            return false;
        }
        return this.dependencies.commitSketchDefinitionChanges(endResult.endedGroupId);
    }
}
